/**
 * Office note log on the work order (iter304).
 *
 * A running, timestamped scratchpad — "Waiting on confirmation from Jason
 * for scheduling". Newest first. Office only: never on a PDF, never on a
 * customer view. Entries can't be deleted; only their author can edit
 * one, and an edit leaves an "edited" stamp.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, Pencil, Check, X, Plus } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { btn } from "@/components/common/btn";
import { listWONoteLog, addWONote, editWONote } from "@/lib/api";

const when = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d) ? "" : d.toLocaleString([], { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
};

export function WONoteLog({ editingWO, onChanged }) {
  const woId = editingWO?.id;
  const [notes, setNotes] = useState([]);
  const [draft, setDraft] = useState("");
  const [composing, setComposing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editText, setEditText] = useState("");
  const me = (localStorage.getItem("abs_current_user_email") || "").toLowerCase();

  const load = useCallback(async () => {
    if (!woId) return;
    try { setNotes((await listWONoteLog(woId)).data || []); } catch { /* office-only */ }
  }, [woId]);
  useEffect(() => { load(); }, [load]);

  if (!woId) return null;

  const add = async () => {
    const text = draft.trim();
    if (!text) return;
    setBusy(true);
    try {
      await addWONote(woId, text);
      setDraft("");
      setComposing(false);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add the note");
    } finally { setBusy(false); }
  };

  const saveEdit = async (id) => {
    const text = editText.trim();
    if (!text) return;
    setBusy(true);
    try {
      await editWONote(woId, id, text);
      setEditing(null);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the note");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="wo-note-log-section">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-base font-semibold text-slate-900">Notes</h3>
        <button type="button" onClick={() => setComposing((c) => !c)} aria-label={composing ? "Close" : "Add a note"} title={composing ? "Close" : "Add a note"}
          className="inline-flex h-6 w-6 items-center justify-center rounded-md border text-slate-500 hover:text-slate-800 hover:bg-slate-100"
          data-testid="btn-new-wo-note">
          {composing ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
        </button>
      </div>

      {composing && (
        <div className="flex items-start gap-2">
          <Textarea
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); add(); } }}
            placeholder="Office only, never printed"
            className="text-sm min-h-[52px]"
            data-testid="input-wo-note-log"
          />
          <button type="button" className={`${btn("write")} mt-0.5`} disabled={busy || !draft.trim()}
                  onClick={add} data-testid="btn-add-wo-note">
            <Send className="w-3.5 h-3.5" />Add
          </button>
        </div>
      )}

      {notes.length === 0 ? (
        !composing && <div className="text-xs text-slate-400" data-testid="wo-note-log-empty">No notes yet.</div>
      ) : (
        <div className="divide-y divide-slate-100 max-h-72 overflow-y-auto" data-testid="wo-note-log-list">
          {notes.map((n) => (
            <div key={n.id} className="py-2 first:pt-0" data-testid={`wo-note-${n.id}`}>
              {editing === n.id ? (
                <div className="space-y-1.5">
                  <Textarea value={editText} onChange={(e) => setEditText(e.target.value)}
                            className="text-sm min-h-[48px]"
                            data-testid={`input-edit-wo-note-${n.id}`} />
                  <div className="flex items-center gap-1.5">
                    <button type="button" className={btn("write")} disabled={busy}
                            onClick={() => saveEdit(n.id)} data-testid={`btn-save-wo-note-${n.id}`}>
                      <Check className="w-3 h-3" />Save
                    </button>
                    <button type="button" className={btn("nav")}
                            onClick={() => setEditing(null)} data-testid={`btn-cancel-wo-note-${n.id}`}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="text-sm text-slate-800 whitespace-pre-wrap break-words">{n.text}</div>
                  <div className="flex items-center gap-2 mt-0.5 text-[11px] text-slate-400">
                    <span>{n.by_name || n.by}</span>
                    <span className="num">{when(n.at)}</span>
                    {n.edited_at && <span className="italic">edited {when(n.edited_at)}</span>}
                    {(n.by || "").toLowerCase() === me && (
                      <button type="button" className="ml-auto text-slate-400 hover:text-slate-700 flex items-center gap-0.5"
                              onClick={() => { setEditing(n.id); setEditText(n.text); }}
                              data-testid={`btn-edit-wo-note-${n.id}`}>
                        <Pencil className="w-2.5 h-2.5" />edit
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
